/**
 * Campaign A/B test tracking — fires GA4 custom events + backend beacon.
 *
 * GA4 events are the primary reporting channel (visible in Google Analytics
 * explorations filtered by campaign_variant). Backend events are a secondary
 * mirror for the admin dashboard.
 */

export type CampaignABEvent =
  | 'campaign_ab_assigned'
  | 'campaign_ab_impression'
  | 'campaign_ab_cta_click'
  | 'campaign_ab_upload_submitted'
  | 'campaign_ab_signup_completed'
  | 'campaign_ab_preview_viewed'
  | 'campaign_ab_top_cta_click'
  | 'campaign_ab_full_analysis_purchased';

export type CampaignAssignmentMethod = 'random' | 'cookie' | 'forced';

const AB_EVENT_MAP: Record<CampaignABEvent, string> = {
  campaign_ab_assigned: 'assigned',
  campaign_ab_impression: 'impression',
  campaign_ab_cta_click: 'cta_click',
  campaign_ab_upload_submitted: 'upload_submitted',
  campaign_ab_signup_completed: 'signup_completed',
  campaign_ab_preview_viewed: 'preview_viewed',
  campaign_ab_top_cta_click: 'top_cta_click',
  campaign_ab_full_analysis_purchased: 'full_analysis_purchased',
};

/**
 * Set the campaign variant as a GA4 user property so all subsequent events
 * and sessions can be segmented by variant in GA4 reports.
 */
export function setCampaignVariantUserProperty(variant: 'a' | 'b'): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('set', 'user_properties', {
      campaign_variant: variant,
    });
  }
}

export function trackCampaignAB(
  event: CampaignABEvent,
  variant: 'a' | 'b',
  assignmentMethod: CampaignAssignmentMethod,
  extra?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;

  // GA4 custom event (primary)
  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('event', event, {
      campaign_variant: variant,
      assignment_method: assignmentMethod,
      test_name: 'campaign_preview_v1',
      ...extra,
    });
  }

  // Backend beacon (secondary — admin dashboard)
  try {
    const body = JSON.stringify({
      test_name: 'campaign_preview_v1',
      variant,
      event_type: AB_EVENT_MAP[event],
      assignment_method: assignmentMethod,
      metadata: { url: window.location.href, ...extra },
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        '/api/ab/homepage/event',
        new Blob([body], { type: 'application/json' }),
      );
    } else {
      fetch('/api/ab/homepage/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    }
  } catch {
    // Tracking must never break user experience
  }
}

/**
 * Track a full analysis purchase with campaign context.
 * Call this from the purchase confirmation flow when we know the user
 * originally came from a campaign page.
 */
export function trackCampaignPurchase(
  variant: 'a' | 'b',
  assignmentMethod: CampaignAssignmentMethod,
  transactionId: string,
  value: number,
): void {
  trackCampaignAB('campaign_ab_full_analysis_purchased', variant, assignmentMethod, {
    transaction_id: transactionId,
    value,
    currency: 'USD',
  });
}

/**
 * Read campaign A/B attribution from localStorage (preview-specific key
 * or legacy generic key) or from server-set cookies.
 */
export function getCampaignAttribution(): {
  variant: 'a' | 'b';
  assignmentMethod: CampaignAssignmentMethod;
} | null {
  if (typeof window === 'undefined') return null;
  for (const key of ['quilty_campaign_preview_session', 'quilty_campaign_session']) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const cs = JSON.parse(raw);
        if (cs.variant && cs.assignmentMethod) return cs;
      }
    } catch { /* ignore */ }
  }
  try {
    const cookies = document.cookie.split('; ');
    const v = cookies.find(c => c.startsWith('campaign_preview_variant='))?.split('=')[1];
    const m = cookies.find(c => c.startsWith('campaign_preview_assignment='))?.split('=')[1];
    if ((v === 'a' || v === 'b') && m) {
      return { variant: v, assignmentMethod: m as CampaignAssignmentMethod };
    }
  } catch { /* ignore */ }
  return null;
}
